/**
 * useLearningProfile Hook
 * 
 * CRUD operations for technician learning profiles (Catalyst feature).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  TechnicianLearningProfile, 
  LearningStyle, 
  LearningConstraint,
  SkillDepth 
} from '@/types/training';

/**
 * Fetch a technician's learning profile
 */
export function useLearningProfile(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['learning-profile', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return null;
      
      const { data, error } = await supabase
        .from('technician_learning_profile')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .maybeSingle();
      
      if (error) throw error;
      
      if (!data) return null;
      
      // Transform to typed structure
      return {
        technician_profile_id: data.technician_profile_id,
        preferred_styles: (data.preferred_styles || []) as LearningStyle[],
        constraints: (data.constraints || []) as LearningConstraint[],
        current_level: (data.current_level || 'foundation') as SkillDepth,
        focus_domains: data.focus_domains || [],
        inferred_styles: (data.inferred_styles || {}) as Record<LearningStyle, number>,
        inferred_confidence: (data.inferred_confidence || {}) as Record<string, number>,
        created_at: data.created_at,
        updated_at: data.updated_at,
      } as TechnicianLearningProfile;
    },
    enabled: !!technicianProfileId,
  });
}

interface UpdateLearningProfileInput {
  technicianProfileId: string;
  preferredStyles?: LearningStyle[];
  constraints?: LearningConstraint[];
  currentLevel?: SkillDepth;
  focusDomains?: string[];
}

/**
 * Create or update a technician's learning profile
 */
export function useUpdateLearningProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: UpdateLearningProfileInput) => {
      const { technicianProfileId, preferredStyles, constraints, currentLevel, focusDomains } = input;
      
      const updateData: Record<string, unknown> = {
        technician_profile_id: technicianProfileId,
      };
      
      if (preferredStyles !== undefined) updateData.preferred_styles = preferredStyles;
      if (constraints !== undefined) updateData.constraints = constraints;
      if (currentLevel !== undefined) updateData.current_level = currentLevel;
      if (focusDomains !== undefined) updateData.focus_domains = focusDomains;
      
      const { data, error } = await supabase
        .from('technician_learning_profile')
        .upsert(updateData as any, {
          onConflict: 'technician_profile_id',
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['learning-profile', variables.technicianProfileId] 
      });
    },
  });
}

/**
 * Update inferred styles based on learning behavior
 */
export function useUpdateInferredStyles() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: {
      technicianProfileId: string;
      inferredStyles: Record<LearningStyle, number>;
      inferredConfidence?: Record<string, number>;
    }) => {
      const { technicianProfileId, inferredStyles, inferredConfidence } = input;
      
      const updateData: Record<string, unknown> = {
        technician_profile_id: technicianProfileId,
        inferred_styles: inferredStyles,
      };
      
      if (inferredConfidence) {
        updateData.inferred_confidence = inferredConfidence;
      }
      
      const { data, error } = await supabase
        .from('technician_learning_profile')
        .upsert(updateData as any, {
          onConflict: 'technician_profile_id',
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['learning-profile', variables.technicianProfileId] 
      });
    },
  });
}
